import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, Pencil, X, ExternalLink } from "lucide-react";

// NON-BLOCKING END-gate confirmation chip: the engine auto-grounds the best-match SCA/DBA wage determination
// and proceeds by default; this surfaces the grounded WD number + covered county so an operator can one-click
// override before pricing locks. It never stops-and-waits.
export function WageDeterminationChip({ deal, isOfficer, reload }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [wdNumber, setWdNumber] = useState("");
  const [revision, setRevision] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/bids/${deal.id}/wage-determination/status`)
      .then((r) => { if (alive) setSt(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [deal.id, deal.wage_determination_source, deal.wd_locate]);

  if (!st || !st.applicable) return null;

  const grounded = st.grounded;
  const tone = st.stale
    ? "bg-red-100 text-red-800 border-red-300"
    : grounded
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : "bg-amber-100 text-amber-800 border-amber-300";
  const where = [st.county, st.state_name || st.state].filter(Boolean).join(", ");

  const override = async () => {
    if (!wdNumber.trim()) { toast.error("Enter the WD number to ground."); return; }
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/wage-determination/ground`, {
        wd_number: wdNumber.trim(), revision: revision.trim() || null, source: "human",
      });
      toast.success(`Wage determination set to ${wdNumber.trim()}`);
      setOpen(false); setWdNumber(""); setRevision("");
      const r = await api.get(`/bids/${deal.id}/wage-determination/status`);
      setSt(r.data); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to ground WD"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 relative inline-block" data-testid="wd-ground-chip">
      <button
        onClick={() => isOfficer && setOpen((o) => !o)}
        disabled={!isOfficer}
        className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${tone} ${isOfficer ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
        title={grounded ? `Grounded ${st.type || ""} WD ${st.wd_number} rev ${st.revision}` : "No WD grounded yet"}
        data-testid="wd-ground-trigger">
        <FileCheck2 className="h-3 w-3" />
        <span className="font-semibold">
          {st.type || "Wage"} WD: {grounded ? `${st.wd_number}${st.revision != null ? ` r${st.revision}` : ""}` : "not grounded"}
        </span>
        {where && <span className="opacity-70 ml-1">· {where}</span>}
        {st.stale && <span className="ml-1 font-semibold">· stale rates</span>}
        {isOfficer && <Pencil className="h-2.5 w-2.5 opacity-60" />}
      </button>
      {grounded && st.revised_date && (
        <div className={`text-[10px] mt-0.5 ${st.stale ? "text-red-600" : "text-zinc-400"}`} data-testid="wd-ground-revised">
          rates last revised {st.revised_date}
          {st.stale && st.stale_note ? ` — ${st.stale_note}` : ""}
        </div>
      )}

      {open && (
        <div className="absolute z-40 mt-1 w-80 bg-white border border-zinc-200 rounded-lg shadow-lg p-3" data-testid="wd-ground-editor">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-zinc-900">Wage determination</span>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-[10px] text-zinc-500 mb-2">
            {grounded ? (
              <>Auto-grounded <span className="font-medium">{st.type} {st.wd_number}{st.revision != null ? ` rev ${st.revision}` : ""}</span>
                {where ? <> for <span className="font-medium">{where}</span></> : null}
                {st.source ? <> ({st.source})</> : null}. Override below only if a different WD applies.</>
            ) : (
              <>A {st.type || "prevailing-wage"} determination applies{where ? <> for <span className="font-medium">{where}</span></> : null} but none is grounded yet. Enter the correct WD number to ground it.</>
            )}
          </p>
          {grounded && st.document_url && (
            <a href={st.document_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mb-2" data-testid="wd-ground-doc-link">
              <ExternalLink className="h-3 w-3" /> View grounded WD document
            </a>
          )}
          <div className="flex gap-2 mb-2">
            <input value={wdNumber} onChange={(e) => setWdNumber(e.target.value)} placeholder="WD number (e.g. 2015-4281)"
                   className="flex-1 bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 outline-none focus:border-gold-dark" data-testid="wd-ground-number" />
            <input value={revision} onChange={(e) => setRevision(e.target.value)} placeholder="Rev"
                   className="w-16 bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 outline-none focus:border-gold-dark" data-testid="wd-ground-revision" />
          </div>
          <div className="flex items-center justify-end">
            <button onClick={override} disabled={busy || !wdNumber.trim()}
                    className="text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50" data-testid="wd-ground-save">
              Ground this WD
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
